import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";

const HERO_PHOTOS = [
  {
    url: "/images/hero/hero-masca.webp",
    alt: "Masca village canyon in Tenerife with dramatic volcanic cliffs and palm trees",
  },
  {
    url: "/images/hero/hero-coastal.webp",
    alt: "Stunning coastal cliffs and deep blue Atlantic ocean from Tenerife highlands",
  },
  {
    url: "/images/hero/hero-los-gigantes.webp",
    alt: "Los Gigantes towering volcanic cliffs with sailboat on turquoise water",
  },
  {
    url: "/images/hero/hero-teide-sunset.webp",
    alt: "Breathtaking orange sunset above the clouds from Mount Teide summit Tenerife",
  },
];

export default function Hero() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (index: number) => {
    if (transitioning || index === activeIndex) return;
    setPrevIndex(activeIndex);
    setTransitioning(true);
    setActiveIndex(index);
    setTimeout(() => {
      setPrevIndex(null);
      setTransitioning(false);
    }, 900);
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % HERO_PHOTOS.length;
        setPrevIndex(prev);
        setTransitioning(true);
        setTimeout(() => {
          setPrevIndex(null);
          setTransitioning(false);
        }, 900);
        return next;
      });
    }, 10000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <section className="relative w-full h-[640px] md:h-[720px] flex items-center justify-center overflow-hidden">

      {/* ─── MOBILE: Crossfade Slideshow ─── */}
      <div className="md:hidden absolute inset-0">
        {HERO_PHOTOS.map((photo, i) => (
          <div
            key={photo.url}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-900"
            style={{
              backgroundImage: `url('${photo.url}')`,
              opacity: i === activeIndex ? 1 : 0,
              transitionDuration: "900ms",
              zIndex: i === activeIndex ? 2 : prevIndex === i ? 1 : 0,
            }}
            aria-hidden={i !== activeIndex}
          />
        ))}
        {/* dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60 z-10" />
      </div>

      {/* ─── DESKTOP / TABLET: editorial mosaic ─── */}
      {/*
        Left half full height: Masca rock formation
        Right top 62%: Teide sunset
        Right bottom 38%: Coastal cliffs | Los Gigantes
      */}
      <div className="hidden md:flex absolute inset-0">
        {/* Left half — Masca canyon, centred on the dramatic rock spire */}
        <div
          className="w-1/2 h-full bg-cover"
          style={{
            backgroundImage: `url('${HERO_PHOTOS[0].url}')`,
            backgroundPosition: "55% 50%",
          }}
        />
        {/* Vertical divider */}
        <div className="absolute inset-y-0 left-1/2 w-px bg-background-50/20 z-10" />
        {/* Right half */}
        <div className="w-1/2 h-full flex flex-col">
          {/* Top-right 62% — Teide sunset centred on sun + horizon */}
          <div
            className="w-full flex-shrink-0 bg-cover"
            style={{
              height: "62%",
              backgroundImage: `url('${HERO_PHOTOS[3].url}')`,
              backgroundPosition: "50% 42%",
            }}
          />
          {/* Horizontal divider */}
          <div className="w-full h-px flex-shrink-0 bg-background-50/20 z-10" />
          {/* Bottom-right 38% — two small panels */}
          <div className="w-full flex flex-1">
            {/* Coastal cliffs — show the pointy sea stacks meeting the ocean */}
            <div
              className="w-1/2 h-full bg-cover"
              style={{
                backgroundImage: `url('${HERO_PHOTOS[1].url}')`,
                backgroundPosition: "30% 60%",
              }}
            />
            {/* Vertical divider */}
            <div className="h-full w-px flex-shrink-0 bg-background-50/20 z-10" />
            {/* Los Gigantes — show cliff face and the sailboat on the water */}
            <div
              className="w-1/2 h-full bg-cover"
              style={{
                backgroundImage: `url('${HERO_PHOTOS[2].url}')`,
                backgroundPosition: "50% 40%",
              }}
            />
          </div>
        </div>
      </div>
      {/* Desktop overlay */}
      <div className="hidden md:block absolute inset-0 bg-gradient-to-b from-black/55 via-black/38 to-black/65 z-10" />

      {/* ─── Content (both layouts) ─── */}
      <div className="relative z-20 w-full max-w-5xl px-4 md:px-6 text-center">
        <div className="inline-flex items-center gap-2 bg-background-50/15 backdrop-blur border border-background-50/25 text-background-50 text-xs md:text-sm px-4 py-1.5 rounded-full mb-6">
          <i className="ri-award-line text-accent-300"></i>
          <span>{t("hero_badge")}</span>
        </div>

        <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-semibold text-background-50 leading-[1.05] mb-5">
          {t("hero_title_1")}<br />
          <span className="text-primary-300">{t("hero_title_2")}</span>
        </h1>

        <p className="text-background-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
          {t("hero_subtitle")}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="#excursions"
            className="bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 px-7 py-3.5 rounded-md font-medium text-sm md:text-base flex items-center gap-2 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("hero_cta_explore")}
            <i className="ri-arrow-right-line"></i>
          </a>
          <a
            href="https://wa.me/48792842613"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 px-7 py-3.5 rounded-md font-medium text-sm md:text-base flex items-center gap-2 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line"></i>
            {t("hero_cta_talk")}
          </a>
        </div>
      </div>

      {/* ─── Mobile: dot indicators ─── */}
      <div className="md:hidden absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {HERO_PHOTOS.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              i === activeIndex
                ? "w-6 h-2 bg-accent-400"
                : "w-2 h-2 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>

      {/* ─── Desktop: subtle photo labels bottom-right ─── */}
      <div className="hidden md:flex absolute bottom-4 right-5 z-20 items-center gap-1.5 text-background-50/50 text-xs">
        <i className="ri-camera-line text-xs"></i>
        <span>Tenerife — Mateo Excursions</span>
      </div>
    </section>
  );
}